import { guideCardToPlainText } from './guide_card_text';

const HTML_ENTITY_MAP = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

const decodeHtmlEntities = (value) => value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity) => {
  const normalized = entity.toLowerCase();
  if (normalized[0] === '#') {
    const codePoint = normalized[1] === 'x'
      ? parseInt(normalized.slice(2), 16)
      : parseInt(normalized.slice(1), 10);

    if (Number.isFinite(codePoint)) {
      return String.fromCodePoint(codePoint);
    }
  }

  return HTML_ENTITY_MAP[normalized] || match;
});

const stripInlineMarkdown = (value) => {
  let output = value;

  output = output.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
  output = output.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  output = output.replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1');
  output = output.replace(/`([^`]+)`/g, '$1');
  output = output.replace(/~~([^~]+)~~/g, '$1');
  output = output.replace(/(\*\*\*|___)(.*?)\1/g, '$2');
  output = output.replace(/(\*\*|__)(.*?)\1/g, '$2');
  output = output.replace(/(^|[\s([{"'])\*([^*\n]+)\*(?=$|[\s)\]}.,!?;:'"])/g, '$1$2');
  output = output.replace(/(^|[\s([{"'])_([^_\n]+)_(?=$|[\s)\]}.,!?;:'"])/g, '$1$2');
  output = output.replace(/<[^>]+>/g, '');
  output = output.replace(/\\([\\`*{}\[\]()#+\-.!_>])/g, '$1');

  return decodeHtmlEntities(output).replace(/[ \t]+/g, ' ').trim();
};

const getIndent = (leadingWhitespace) => {
  const spaces = leadingWhitespace.replace(/\t/g, '  ').length;
  return '  '.repeat(Math.floor(spaces / 2));
};

const isTableDivider = (line) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const formatTableRow = (line) => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map(cell => stripInlineMarkdown(cell.trim()))
  .join('\t');

const pushBlankLine = (lines) => {
  if (lines.length > 0 && lines[lines.length - 1] !== '') {
    lines.push('');
  }
};

const pushHeading = (lines, heading) => {
  pushBlankLine(lines);
  lines.push(stripInlineMarkdown(heading));
  pushBlankLine(lines);
};

const markdownToPlainText = (content, { renderCards = false } = {}) => {
  const raw = typeof content === 'string' ? content : String(content || '');
  if (!raw.trim()) return '';

  const output = [];
  const lines = raw.replace(/\r\n?/g, '\n').split('\n');
  let fence = null;

  for (let index = 0; index < lines.length; index += 1) {
    let line = lines[index];
    let trimmed = line.trim();

    if (fence) {
      if (new RegExp(`^\\s{0,3}${fence[0]}{${fence.length},}\\s*$`).test(line)) {
        fence = null;
        pushBlankLine(output);
      } else {
        output.push(decodeHtmlEntities(line).replace(/\s+$/g, ''));
      }
      continue;
    }
    const opening = line.match(/^ {0,3}(`{3,}|~{3,})(.*)$/);
    if (opening && (opening[1][0] !== '`' || !opening[2].includes('`'))) {
      pushBlankLine(output);
      fence = opening[1];
      if (renderCards) {
        const close = new RegExp(`^\\s{0,3}${fence[0]}{${fence.length},}\\s*$`);
        let end = index + 1;
        while (end < lines.length && !close.test(lines[end])) end += 1;
        const card = end < lines.length
          ? guideCardToPlainText(opening[2], lines.slice(index + 1, end).join('\n'))
          : null;
        if (card !== null) {
          output.push(card);
          pushBlankLine(output);
          index = end;
          fence = null;
        }
      }
      continue;
    }
    line = line
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, '\n')
      .replace(/<(p|div|li|h[1-6]|blockquote|tr)[^>]*>/gi, '');
    trimmed = line.trim();

    if (!trimmed) {
      pushBlankLine(output);
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed) || isTableDivider(line)) {
      continue;
    }

    const headingMatch = line.match(/^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/);
    if (headingMatch) {
      pushHeading(output, headingMatch[1]);
      continue;
    }

    const blockquoteLine = line.replace(/^\s*>+\s?/, '');
    const orderedMatch = blockquoteLine.match(/^(\s*)(\d+)[.)]\s+(.+)$/);
    if (orderedMatch) {
      output.push(`${getIndent(orderedMatch[1])}${orderedMatch[2]}. ${stripInlineMarkdown(orderedMatch[3]).replace(/^\[[ x]\]\s+/i, '')}`);
      continue;
    }

    const unorderedMatch = blockquoteLine.match(/^(\s*)[-*+]\s+(.+)$/);
    if (unorderedMatch) {
      output.push(`${getIndent(unorderedMatch[1])}- ${stripInlineMarkdown(unorderedMatch[2]).replace(/^\[[ x]\]\s+/i, '')}`);
      continue;
    }

    if (/^\s*\|.*\|\s*$/.test(line)) {
      output.push(formatTableRow(line));
      continue;
    }

    output.push(stripInlineMarkdown(blockquoteLine));
  }

  return output
    .join('\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export default markdownToPlainText;
